import type { ApolloClient } from "@apollo/client";
import type { TagFormValues } from "./types";
import { TagTypeQuery, type Tag } from "@/partner/entities/tags";
import {
  CustomFieldTypeType,
  type CreateTagInputType,
  type UpdateTagInputType,
  type TagTypeQueryType,
} from "@/types/graphql/partner.generated";

/** Определение кастомного поля выбранного типа тега (`TagType.customFields`) */
export type TagCustomFieldDefinition =
  TagTypeQueryType["tagType"]["customFields"][number];

function toValuesRecord(
  values: Tag["customFieldValues"] | undefined
): Record<string, unknown> {
  return Object.fromEntries(
    (values ?? []).map((field) => [field.name, field.value])
  );
}

/**
 * Строит массив значений для мутации строго по определениям выбранного
 * типа: значения полей чужого типа (после смены типа) и значения полей,
 * убранных из типа админом (в базе остаются, спека §6), в форме тега могут
 * присутствовать в состоянии, но бэкенд отвечает `TAG_CUSTOM_FIELD_INVALID`
 * ("no such field in the tag type") на любое лишнее имя, поэтому здесь
 * никогда не берём ключи из `values` напрямую, только из `definitions`.
 *
 * Булево поле, к которому пользователь не притронулся, в состоянии формы
 * вообще не имеет ключа (переключатель не задаёт значение по умолчанию).
 * Бэкенд трактует отсутствие значения как "поле не заполнено" и требует
 * его для required, хотя выключенный тумблер это осознанный `false`, а не
 * "нет ответа". Поэтому булевы поля всегда приводим к явному true/false.
 */
function toValuesList(
  values: TagFormValues["customFieldValues"],
  definitions: TagCustomFieldDefinition[]
): { name: string; value: unknown }[] {
  const record = values ?? {};
  const result: { name: string; value: unknown }[] = [];

  for (const definition of definitions) {
    const raw = record[definition.name];

    if (definition.type === CustomFieldTypeType.BooleanType) {
      result.push({ name: definition.name, value: Boolean(raw) });
      continue;
    }

    if (raw !== undefined) {
      result.push({ name: definition.name, value: raw });
    }
  }

  return result;
}

/**
 * Определения кастомных полей выбранного типа на момент сохранения.
 * `TagTypeQuery` к этому моменту уже загружен и закеширован Apollo:
 * ровно по нему `customFields`-поле формы строит видимые поля, поэтому
 * `cache-first` в норме не делает лишний сетевой запрос.
 */
export async function getTagTypeCustomFields(
  client: ApolloClient<object>,
  typeId: string | null | undefined
): Promise<TagCustomFieldDefinition[]> {
  if (!typeId) return [];

  const { data } = await client.query<TagTypeQueryType>({
    query: TagTypeQuery,
    variables: { id: typeId },
    fetchPolicy: "cache-first",
  });

  return data?.tagType?.customFields ?? [];
}

/**
 * Maps GraphQL Tag data to form values
 */
export const mapTagToFormValues = (
  tag: Tag | null | undefined
): TagFormValues => {
  if (!tag) {
    return {
      title: "",
      description: null,
      typeId: null,
      platformPageId: null,
      image: { horizontalId: null, verticalId: null },
      customFieldValues: {},
    };
  }

  return {
    title: tag.title || "",
    description: tag.description || null,
    typeId: tag.type?.id || null,
    platformPageId: tag.platformPage?.id || null,
    image: {
      horizontalId: tag.image?.id || null,
      verticalId: tag.verticalImage?.id || null,
    },
    customFieldValues: toValuesRecord(tag.customFieldValues),
  };
};

/**
 * Maps form values to CreateTagInput for mutation
 */
export const mapFormValuesToCreateTagInput = (
  values: TagFormValues,
  customFields: TagCustomFieldDefinition[]
): CreateTagInputType => {
  return {
    title: values.title,
    description: values.description || undefined,
    typeId: values.typeId || undefined,
    imageId: values.image?.horizontalId || undefined,
    verticalImageId: values.image?.verticalId || undefined,
    platformPageId: values.platformPageId || undefined,
    customFieldValues: toValuesList(values.customFieldValues, customFields),
  };
};

/**
 * Maps form values to UpdateTagInput for mutation
 */
export const mapFormValuesToUpdateTagInput = (
  values: TagFormValues,
  customFields: TagCustomFieldDefinition[]
): UpdateTagInputType => {
  return {
    title: values.title,
    description: values.description ?? undefined,
    typeId: values.typeId ?? undefined,
    imageId: values.image?.horizontalId ?? null,
    verticalImageId: values.image?.verticalId ?? null,
    platformPageId: values.platformPageId ?? null,
    customFieldValues: toValuesList(values.customFieldValues, customFields),
  };
};
